import { useEffect, useRef, useState } from 'react';
import * as T from 'three';
import { FURNITURE_BY_ID, footprint, isLondonNight, placementProblem, type RoomShape } from '@london-life/contracts';
import type { Character } from '@london-life/game/game';
import { buildRoom, homeProps, type RoomThing } from '@london-life/game/house/room';
import { createStage } from '@london-life/game/scene/stage';
import { VenueCrowd } from '@london-life/game/venue/crowd';
import { findPath, walkGrid } from '@london-life/game/venue/venues';
import { offerSnapshot } from '../lib/camera';

type Props = {
  // the room: its floor and colours (the home's kind, after any extensions)
  room: RoomShape;
  placed: RoomThing[];
  character: Character;
  dataSaver?: boolean;
  // Buy mode: things can be picked up and dragged across the floor, and the floor shows its grid
  editing?: boolean;
  selectedId?: string | null;
  onSelect?: (id: string | null) => void;
  // a thing was dropped (the parent checks the spot and keeps or refuses it)
  onDrop?: (id: string, x: number, z: number) => void;
  // bumped when a drop was refused, so the room is redrawn from `placed`
  revision?: number;
  // outside Buy mode: a thing was tapped; the answer is what to do once the character gets there (or null)
  onThing?: (id: string) => (() => void) | null;
  // a tapped spot the character cannot get to
  onBlocked?: () => void;
  // the character is busy with something (it keeps at it where it stands)
  busy?: boolean;
  // how far up the screen the room sits (a sheet covers the bottom)
  lift?: number;
  // others in the home (the host, guests): they stand about the room, with their @name over them
  people?: { id: string; character: Character; tag: string }[];
  // your own @name over you (a visit)
  myTag?: string;
};
const ME = 'me';
// a home is small: the character may brush past the furniture
const HOME_MARGIN = 0.14;

// the ring under a picked-up thing: green where it fits, red where it does not (palette tokens, read once)
export const colourToken = (name: string, fallback: string) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
// the sky behind a room, from the palette: soft blue by day, deep navy at night
export const roomSky = (night = false) =>
  night
    ? colourToken('--ll-night', '#0e1830')
    : '#' +
      new T.Color(colourToken('--ll-sky-soft', '#effaff'))
        .lerp(new T.Color(colourToken('--ll-sky', '#4f9fc3')), 0.22)
        .getHexString();

// The home in 3D, the way Lagos Life shows it: the whole room fills the screen, the character walks where the
// floor is tapped (round the furniture) and goes to a thing to use it. In Buy mode a finger or the mouse on a
// thing drags it across a grid instead of turning the room. Without WebGL a still picture is drawn.
export function HouseView({
  room: shape,
  placed,
  character,
  dataSaver = false,
  editing = false,
  selectedId,
  onSelect,
  onDrop,
  revision = 0,
  onThing,
  onBlocked,
  busy = false,
  lift = 0.2,
  people = [],
  myTag,
}: Props) {
  const container = useRef<HTMLDivElement>(null);
  // name tags over heads, moved every frame
  const overlay = useRef(new Map<string, HTMLElement>());
  const [ready, setReady] = useState(false);
  const engine = useRef<{
    replace: (room: RoomShape, things: RoomThing[]) => void;
    saver: (on: boolean) => void;
    select: (id: string | null) => void;
    lift: (value: number) => void;
    grid: (on: boolean) => void;
    crowd: VenueCrowd;
  } | null>(null);
  const [mode, setMode] = useState<'3d' | 'still' | 'loading'>('loading');
  const latest = useRef({ room: shape, placed, editing, onSelect, onDrop, onThing, onBlocked });
  latest.current = { room: shape, placed, editing, onSelect, onDrop, onThing, onBlocked };
  const placedKey = JSON.stringify(placed);
  const roomKey = JSON.stringify(shape);
  useEffect(() => {
    const element = container.current;
    if (!element) return;
    let room: ReturnType<typeof buildRoom> | undefined;
    let started = false;
    // the home follows London's clock: dark outside and the lamps on after 7 pm
    const night = isLondonNight();
    const crowd = new VenueCrowd(walkGrid({ floor: shape.floor, props: homeProps(placed) }, HOME_MARGIN));
    const stage = createStage(element, {
      sky: roomSky(night),
      night,
      dataSaver,
      ready: () => started,
      fps: 30,
      tick: (dt) => {
        crowd.update(dt);
        for (const [id, tag] of overlay.current) {
          const at = crowd.headOnScreen(id, stage.camera, stage.size.width, stage.size.height);
          if (!at) {
            tag.style.visibility = 'hidden';
            continue;
          }
          tag.style.visibility = 'visible';
          const half = tag.offsetWidth / 2 + 4;
          const x = Math.min(Math.max(at.x, half), stage.size.width - half);
          tag.style.transform = `translate(${Math.round(x)}px, ${Math.round(at.y)}px) translate(-50%, -100%)`;
        }
      },
    });
    const { scene, camera, canvas, controls, requestStill } = stage;
    setMode(stage.mode);
    scene.add(crowd.group);
    let fittedLift = lift;
    // the ring under the selected thing
    const okColour = new T.Color(colourToken('--ll-green', '#15803d')),
      badColour = new T.Color(colourToken('--ll-red', '#e03131'));
    const ringMaterial = new T.MeshBasicMaterial({
      color: okColour,
      transparent: true,
      opacity: 0.45,
      depthWrite: false,
    });
    const ringGeometry = new T.PlaneGeometry(1, 1);
    const ring = new T.Mesh(ringGeometry, ringMaterial);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.03;
    ring.visible = false;
    scene.add(ring);
    // the floor grid in Buy mode (half-metre squares)
    const gridMaterial = new T.LineBasicMaterial({
      color: new T.Color(colourToken('--foreground', '#1d2433')),
      transparent: true,
      opacity: 0.16,
    });
    let gridLines: T.LineSegments | null = null;
    let showGrid = false;
    const drawGrid = (floor: { w: number; d: number }) => {
      if (gridLines) {
        scene.remove(gridLines);
        gridLines.geometry.dispose();
      }
      const points: number[] = [];
      for (let x = -floor.w / 2; x <= floor.w / 2 + 0.001; x += 0.5)
        points.push(x, 0.025, -floor.d / 2, x, 0.025, floor.d / 2);
      for (let z = -floor.d / 2; z <= floor.d / 2 + 0.001; z += 0.5)
        points.push(-floor.w / 2, 0.025, z, floor.w / 2, 0.025, z);
      const geometry = new T.BufferGeometry();
      geometry.setAttribute('position', new T.Float32BufferAttribute(points, 3));
      gridLines = new T.LineSegments(geometry, gridMaterial);
      gridLines.visible = showGrid;
      scene.add(gridLines);
    };
    let selected: string | null = null;
    const showRing = (id: string | null, at?: { x: number; z: number }, fits = true) => {
      const thing = latest.current.placed.find((entry) => entry.id === id);
      const item = thing && FURNITURE_BY_ID.get(thing.item);
      if (!thing || !item) {
        ring.visible = false;
        return;
      }
      const foot = footprint(item, thing.rotation);
      ring.scale.set(foot.w + 0.3, foot.d + 0.3, 1);
      ring.position.x = at?.x ?? thing.x;
      ring.position.z = at?.z ?? thing.z;
      ringMaterial.color.copy(fits ? okColour : badColour);
      ring.visible = true;
    };
    const replace = (nextRoom: RoomShape, nextThings: RoomThing[]) => {
      const first = !room;
      if (room) {
        scene.remove(room.group);
        room.dispose();
      }
      room = buildRoom(nextRoom, nextThings, {
        night,
        rich: !stage.slow,
        models: stage.mode === '3d' && !stage.slow && !dataSaver,
      });
      scene.add(room.group);
      crowd.setGrid(walkGrid({ floor: room.floor, props: homeProps(nextThings) }, HOME_MARGIN));
      drawGrid(room.floor);
      if (first) {
        stage.fit(room.floor, fittedLift);
        crowd.add(ME, character, room.standing, false);
      }
      showRing(selected);
      requestStill();
    };

    // ---- taps and dragging
    const raycaster = new T.Raycaster();
    const floorPlane = new T.Plane(new T.Vector3(0, 1, 0), 0);
    const pointer = new T.Vector2();
    const hit = new T.Vector3();
    const floorPointAt = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      pointer.set(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycaster.setFromCamera(pointer, camera);
      return raycaster.ray.intersectPlane(floorPlane, hit) ? { x: hit.x, z: hit.z } : null;
    };
    const thingAt = (event: PointerEvent) => {
      if (!room) return null;
      floorPointAt(event);
      const pieces = [...room.byId.values()];
      const [first] = raycaster.intersectObjects(pieces, true);
      let object: T.Object3D | null = first?.object ?? null;
      while (object && !room.byId.has(object.name)) object = object.parent;
      return object?.name ?? null;
    };
    let drag: {
      id: string;
      offsetX: number;
      offsetZ: number;
      moved: boolean;
      x: number;
      z: number;
      pointerId: number;
    } | null = null;
    // a tap (not a turn of the room) outside Buy mode: walk there, or go and use the thing
    let press: { x: number; y: number; id: number } | null = null;
    const down = (event: PointerEvent) => {
      if (!event.isPrimary) return;
      if (!latest.current.editing) {
        press = { x: event.clientX, y: event.clientY, id: event.pointerId };
        return;
      }
      const id = thingAt(event);
      if (!id) {
        if (selected) {
          selected = null;
          showRing(null);
          latest.current.onSelect?.(null);
        }
        return;
      }
      const thing = latest.current.placed.find((entry) => entry.id === id)!;
      const point = floorPointAt(event);
      if (!point) return;
      drag = {
        id,
        offsetX: thing.x - point.x,
        offsetZ: thing.z - point.z,
        moved: false,
        x: thing.x,
        z: thing.z,
        pointerId: event.pointerId,
      };
      controls.enabled = false;
      canvas.setPointerCapture(event.pointerId);
      selected = id;
      showRing(id);
      latest.current.onSelect?.(id);
    };
    const move = (event: PointerEvent) => {
      if (!drag || event.pointerId !== drag.pointerId || !room) return;
      const point = floorPointAt(event);
      if (!point) return;
      const thing = latest.current.placed.find((entry) => entry.id === drag!.id)!;
      const item = FURNITURE_BY_ID.get(thing.item)!;
      const foot = footprint(item, thing.rotation);
      // things snap to the quarter-metre and stay inside the room while they are carried
      const snap = (value: number) => Math.round(value * 4) / 4;
      const x = T.MathUtils.clamp(
        snap(point.x + drag.offsetX),
        -room.floor.w / 2 + foot.w / 2,
        room.floor.w / 2 - foot.w / 2,
      );
      const z = T.MathUtils.clamp(
        snap(point.z + drag.offsetZ),
        -room.floor.d / 2 + foot.d / 2,
        room.floor.d / 2 - foot.d / 2,
      );
      drag.moved = drag.moved || Math.hypot(x - thing.x, z - thing.z) > 0.02;
      drag.x = x;
      drag.z = z;
      room.move(drag.id, x, z, thing.rotation);
      const others = latest.current.placed.map((entry) => (entry.id === drag!.id ? { ...entry, x, z } : entry));
      showRing(drag.id, { x, z }, placementProblem(latest.current.room.floor, others) === null);
      requestStill();
    };
    const up = (event: PointerEvent) => {
      if (press && event.pointerId === press.id) {
        const tap = Math.hypot(event.clientX - press.x, event.clientY - press.y) < 8;
        press = null;
        if (!tap || latest.current.editing) return;
        const id = thingAt(event);
        const to = floorPointAt(event);
        const from = crowd.at(ME);
        if (id) {
          const action = latest.current.onThing?.(id);
          const thing = latest.current.placed.find((entry) => entry.id === id);
          if (action && thing) {
            crowd.walkTo(ME, { x: thing.x, z: thing.z }, () => {
              crowd.face(ME, thing);
              action();
            });
          }
          return;
        }
        if (!to || !from || !room) return;
        if (Math.abs(to.x) > room.floor.w / 2 || Math.abs(to.z) > room.floor.d / 2) return;
        const grid = walkGrid({ floor: room.floor, props: homeProps(latest.current.placed) }, HOME_MARGIN);
        const path = findPath(grid, from, to);
        const end = path.at(-1);
        // nowhere near where the finger went: the furniture is in the way
        if (!end || Math.hypot(end.x - to.x, end.z - to.z) > 0.6) {
          if (Math.hypot(from.x - to.x, from.z - to.z) > 0.4) latest.current.onBlocked?.();
          return;
        }
        crowd.walkTo(ME, to);
        return;
      }
      if (!drag || event.pointerId !== drag.pointerId) return;
      const { id, moved, x, z } = drag;
      drag = null;
      controls.enabled = true;
      if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
      if (moved) latest.current.onDrop?.(id, Math.round(x * 100) / 100, Math.round(z * 100) / 100);
      else showRing(id);
    };
    canvas.addEventListener('pointerdown', down);
    canvas.addEventListener('pointermove', move);
    canvas.addEventListener('pointerup', up);
    canvas.addEventListener('pointercancel', up);

    engine.current = {
      replace,
      saver: stage.saver,
      select: (id) => {
        selected = id;
        showRing(id);
        requestStill();
      },
      lift: (value) => {
        fittedLift = value;
        if (room) stage.fit(room.floor, value);
      },
      grid: (on) => {
        showGrid = on;
        if (gridLines) gridLines.visible = on;
        requestStill();
      },
      crowd,
    };
    replace(shape, placed);
    started = true;
    setReady(true);
    requestStill();
    // the in-game camera can take the home as it is
    const withdraw = offerSnapshot(() => stage.snapshot());
    return () => {
      withdraw();
      engine.current = null;
      setReady(false);
      canvas.removeEventListener('pointerdown', down);
      canvas.removeEventListener('pointermove', move);
      canvas.removeEventListener('pointerup', up);
      canvas.removeEventListener('pointercancel', up);
      crowd.dispose();
      room?.dispose();
      ringGeometry.dispose();
      ringMaterial.dispose();
      gridLines?.geometry.dispose();
      gridMaterial.dispose();
      stage.dispose();
    };
    // the character and the first room are set once; later rooms arrive through replace()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    engine.current?.replace(shape, placed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomKey, placedKey, revision]);
  useEffect(() => {
    engine.current?.select(editing ? (selectedId ?? null) : null);
    engine.current?.grid(editing);
  }, [editing, selectedId]);
  useEffect(() => {
    engine.current?.saver(dataSaver);
  }, [dataSaver]);
  useEffect(() => {
    engine.current?.lift(lift);
  }, [lift]);
  // the others in the home come and go: those already here stand about the room, newcomers wander in
  const peopleKey = people.map((person) => person.id).join(',');
  useEffect(() => {
    const crowd = engine.current?.crowd;
    if (!crowd || !ready) return;
    const wanted = new Set(people.map((person) => person.id));
    for (const id of crowd.ids()) if (id !== ME && !wanted.has(id)) crowd.remove(id);
    const { w, d } = shape.floor;
    for (const person of people)
      if (!crowd.has(person.id))
        crowd.add(
          person.id,
          person.character,
          { x: (Math.random() - 0.5) * w * 0.6, z: (Math.random() - 0.5) * d * 0.6 },
          true,
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peopleKey, ready, roomKey]);
  const keep = (id: string) => (element: HTMLElement | null) => {
    if (element) overlay.current.set(id, element);
    else overlay.current.delete(id);
  };
  // busy with something: keep at it
  useEffect(() => {
    const crowd = engine.current?.crowd;
    if (!crowd || !busy) return;
    crowd.act(ME, 'interact');
    const timer = window.setInterval(() => crowd.act(ME, 'interact'), 3200);
    return () => window.clearInterval(timer);
  }, [busy]);
  const label = editing ? 'Your room in 3D: drag a thing to move it' : 'The room in 3D: tap the floor to walk';
  return (
    <>
      <div
        className="house-view"
        data-renderer={mode}
        data-editing={editing || undefined}
        ref={container}
        aria-label={label}
      />
      <div className="venue-overlay house-overlay" aria-hidden="true">
        {people.map((person) => (
          <span key={person.id} className="venue-tag" ref={keep(person.id)}>
            {person.tag}
          </span>
        ))}
        {myTag && (
          <span className="venue-tag mine" ref={keep(ME)}>
            {myTag}
          </span>
        )}
      </div>
    </>
  );
}
